import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  User,
  Mail,
  Briefcase,
  MapPin,
  Sun,
  Moon,
  Save,
  LogOut,
  Sparkles,
  Award,
  Globe,
  Phone,
  Calendar,
  IdCard,
} from 'lucide-react';

export const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await authAPI.updateProfile({ name });
      updateUser({ name });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 pb-20 lg:pb-0" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Profile & Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account settings and preferences
          </p>
        </div>

        {/* Profile Card */}
        <Card data-testid="profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar */}
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20 border-4 border-primary/20">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span className="font-semibold text-amber-500">{user?.points || 0} pts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award className="w-4 h-4 text-purple-500" />
                    <span className="text-sm">{user?.badges?.length || 0} badges</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Edit Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="pl-10 bg-muted"
                  />
                </div>
              </div>

              {user?.mobile_number && (
                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={user.mobile_number}
                      disabled
                      className="pl-10 bg-muted"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={user?.role?.replace('_', ' ') || ''}
                      disabled
                      className="pl-10 bg-muted capitalize"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={user?.region || 'Not assigned'}
                      disabled
                      className="pl-10 bg-muted"
                    />
                  </div>
                </div>
              </div>

              {(user?.employee_id || user?.gender || user?.date_of_birth || user?.date_of_joining) && (
                <>
                  <Separator className="my-4" />
                  <div className="grid grid-cols-2 gap-4">
                    {user?.employee_id && (
                      <div className="space-y-2">
                        <Label>Employee ID</Label>
                        <div className="relative">
                          <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={user.employee_id}
                            disabled
                            className="pl-10 bg-muted font-mono"
                          />
                        </div>
                      </div>
                    )}
                    {user?.gender && (
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={user.gender.charAt(0).toUpperCase() + user.gender.slice(1)}
                            disabled
                            className="pl-10 bg-muted capitalize"
                          />
                        </div>
                      </div>
                    )}
                    {user?.date_of_birth && (
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={new Date(user.date_of_birth).toLocaleDateString()}
                            disabled
                            className="pl-10 bg-muted"
                          />
                        </div>
                      </div>
                    )}
                    {user?.date_of_joining && (
                      <div className="space-y-2">
                        <Label>Date of Joining</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={new Date(user.date_of_joining).toLocaleDateString()}
                            disabled
                            className="pl-10 bg-muted"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card data-testid="preferences-card">
          <CardHeader>
            <CardTitle>{t('profile.title')} - {t('settings.general')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Language Preference */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">{t('profile.languagePreference')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('profile.selectLanguage')}
                  </p>
                </div>
              </div>
              <LanguageSelector variant="list" />
            </div>
            
            <Separator />
            
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-blue-500" />
                ) : (
                  <Sun className="w-5 h-5 text-amber-500" />
                )}
                <div>
                  <p className="font-medium">{t('profile.darkMode')}</p>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'dark' ? t('profile.lightMode') : t('profile.darkMode')}
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="border-destructive/50" data-testid="logout-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('common.logout')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('auth.signOut')}
                </p>
              </div>
              <Button variant="destructive" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
